// Copyright (c) 2022 by Move Closer

import { ComponentInternalInstance, computed, PropType, ref } from '@vue/composition-api'
import { RawLocation } from 'vue-router'

import {
  canBeDisabledProp,
  canHaveThemeProp,
  hasVariantProp,
  ThemeRegistry,
  useThemeClass,
  useVariantClass,
  VariantRegistry
} from '../../composables'
import { ComponentObjectPropsOptions } from '../../vue-api'
import { ObjectKeyLiterals } from '../../composables/use-class-registry'

import { AbstractLinkProps, AbstractUseLinkProvides, TabOption } from '../../abstract/components/Link/Link.contracts'
import { LinkProps } from './Link.contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export const linkProps: ComponentObjectPropsOptions<AbstractLinkProps> = {
  ...canBeDisabledProp,
  ...canHaveThemeProp,
  ...hasVariantProp,

  label: {
    type: String,
    required: false
  },

  onClick: {
    type: Function,
    required: false,
    default: () => {
      return () => undefined
    }
  },

  tabTarget: {
    type: String as PropType<TabOption>,
    required: false
  },

  target: {
    type: [String, Object] as PropType<RawLocation>,
    required: false
  },

  title: {
    type: String,
    required: false
  }
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (edited)
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export const useLink = <T extends ObjectKeyLiterals> (
  props: LinkProps,
  internalInstance: ComponentInternalInstance | null,
  themeClassRegistry: ThemeRegistry<T>,
  variantClassRegistry: VariantRegistry<T>
): AbstractUseLinkProvides => {
  const { target, tabTarget, title } = props
  if (internalInstance === null) {
    throw new Error('useLink(): FATAL ERROR! Failed to resolve the component instance!')
  }

  const { proxy, type } = internalInstance
  const theme = ref<string>(typeof props.theme === 'string' ? props.theme : '')
  const variant = ref<string>(typeof props.variant === 'string' ? props.variant : '')

  const { themeClass } = useThemeClass<string>(theme, themeClassRegistry, `${type.componentTag}`)
  const { variantClass } = useVariantClass<string>(variant, variantClassRegistry, `${type.componentTag}`)

  const _title = computed<string>(() => {
    const parts: string [] = title ? [title] : []
    const htmlTarget = typeof tabTarget === 'undefined' ? TabOption.Self : tabTarget
    switch (htmlTarget) {
      case TabOption.Blank:
        parts.unshift(proxy.$t('dsl.link.blank').toString())
        break
      case TabOption.Self:
        if (parts.length) {
          parts.unshift(proxy.$t('dsl.link.self').toString())
        }
        break
    }

    return parts.join(': ')
  })

  const hasCorrectTarget = computed<boolean>(() => {
    if (typeof target === 'undefined') {
      return false
    }

    if (typeof target === 'object' && target === null) {
      return false
    }

    if (typeof target === 'string' && target.length === 0) {
      return false
    }

    return true
  })

  const isExternal = computed<boolean>(() => {
    const toCheck: string = typeof target !== 'string' ? `${target?.path}` : target
    const externalPrefixes: string[] = ['http://', 'https://', 'tel:', 'mailto:']

    if (!toCheck) {
      return false
    }

    for (const p of externalPrefixes) {
      if (toCheck.toLowerCase().startsWith(p)) {
        return true
      }
    }

    return false
  })

  const linkComponent = hasCorrectTarget.value
    ? props.isDisabled
      ? 'div' : 'a'
    : 'div'

  return { _title, hasCorrectTarget, isExternal, linkComponent, themeClass, variantClass }
}
